import { cva, VariantProps } from 'class-variance-authority';
import { useFormikContext } from 'formik';
import { ButtonHTMLAttributes } from 'react';
import { useTranslation } from 'react-i18next';

type SubmitButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  children?: React.ReactNode;
  className?: string;
  enabled?: boolean;
  withFormikContext?: boolean;
  alt?: boolean;
  loading?: boolean;
  loadingText?: string;
};

export type ButtonProps = VariantProps<typeof buttonStyles> &
  ButtonHTMLAttributes<HTMLButtonElement>;

const buttonStyles = cva(
  [
    'disabled:opacity-50',
    'transition-colors',
    'duration-200',
    'hover:duration-500',
  ],
  {
    variants: {
      intent: {
        primary: ['bg-brand', 'text-white', 'hover:bg-brand-dark'],
        secondary: ['border-brand', 'text-brand', 'border', 'bg-white'],
        delete: ['bg-delete', 'text-white'],
        text: ['text-brand hover:underline'],
      },
      size: {
        block: [
          'mb-4',
          'w-full',
          'p-4',
          'rounded-lg',
          'font-semibold',
          'last:mb-0',
        ],
        dynamic: ['flex-1', 'rounded-lg', 'font-semibold', 'py-4'],
        small: ['py-2', 'text-sm', 'font-medium'],
      },
      round: {
        true: ['rounded-full', 'px-8 py-2', 'font-normal', 'text-sm', 'shadow'],
        false: ['uppercase'],
      },
      loading: {
        true: [],
      },
    },
    compoundVariants: [
      {
        intent: 'primary',
        size: 'block',
        className: ['shadow-[0px_4px_5px_rgba(0,0,0,0.16)]'],
      },
      {
        intent: 'text',
        size: 'small',
        round: false,
        className: ['normal-case', 'px-0'],
      },
      {
        intent: 'primary',
        size: 'small',
        round: false,
        className: ['px-5'],
      },
      { size: 'small', round: false, className: ['rounded-md'] },
    ],
    defaultVariants: {
      intent: 'primary',
      size: 'block',
    },
  }
);

export const Button = ({
  intent,
  size,
  round = false,
  disabled,
  loading,
  children,
  className,
  loadingText,
  ...props
}: ButtonProps & { loadingText?: string }) => {
  const { t } = useTranslation();

  // Extract data-usetiful if it was passed explicitly
  const { 'data-usetiful': customDataUsetiful, ...otherProps } = props as any;

  return (
    <button
      {...otherProps}
      data-usetiful={
        customDataUsetiful ?? (children ? children.toString() : t('submit'))
      }
      className={buttonStyles({
        className,
        intent,
        size,
        round,
      })}
      // style={alt ? undefined : { boxShadow: '0px 4px 5px #00000029' }}
      disabled={Boolean(disabled || loading)}
    >
      {loading ? (
        <div className='flex items-center justify-center gap-2'>
          <svg
            className='h-4 w-4 animate-spin'
            xmlns='http://www.w3.org/2000/svg'
            fill='none'
            viewBox='0 0 24 24'
          >
            <circle
              className='opacity-25'
              cx='12'
              cy='12'
              r='10'
              stroke='currentColor'
              strokeWidth='4'
            ></circle>
            <path
              className='opacity-75'
              fill='currentColor'
              d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
            ></path>
          </svg>
          {loadingText || t('loading')}
        </div>
      ) : (
        children ?? t('submit')
      )}
    </button>
  );
};

const WithFormikContext = ({
  children,
  alt,
  className,
  enabled = true,
  loading = false,
  loadingText,
  ...props
}: SubmitButtonProps) => {
  const { submitForm, isSubmitting } = useFormikContext();

  return (
    <Button
      type='button'
      intent={alt ? 'secondary' : 'primary'}
      className={className}
      onClick={submitForm}
      disabled={!enabled || isSubmitting}
      {...props}
      loading={loading || isSubmitting}
      loadingText={loadingText}
    >
      {children}
    </Button>
  );
};

export const SubmitButton = ({
  children,
  className,
  alt,
  enabled = true,
  withFormikContext = true,
  loading = false,
  loadingText,
  ...props
}: SubmitButtonProps) => {
  if (withFormikContext) {
    return (
      <WithFormikContext
        enabled={enabled}
        loading={loading}
        loadingText={loadingText}
        {...props}
      >
        {children}
      </WithFormikContext>
    );
  }

  return (
    <Button
      intent={alt ? 'secondary' : 'primary'}
      className={className}
      type='submit'
      disabled={!enabled}
      loading={loading}
      loadingText={loadingText}
    >
      {children}
    </Button>
  );
};
